import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, ChevronDown } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { SEO } from '../components/SEO';

export const FAQ = () => {
  const faqs = [
    {
      question: 'What is your shipping policy?',
      answer: (
        <div className="space-y-3">
          <p>We are committed to delivering your order in a timely and secure manner.</p>
          <p>All orders are processed within <strong>1-2 working days</strong> (excluding weekends and public holidays).</p>
          <p><strong>UK Delivery:</strong> 3-5 working days, fully tracked.</p>
          <p><strong>EU & International:</strong> 5-10 working days. Customers responsible for customs duties and VAT.</p>
          <p>We are responsible until delivery is completed. Delays may occur due to courier or customs issues.</p>
          <Link to="/shipping-policy" className="inline-block text-[#2F5D8C] hover:underline font-semibold mt-2">Read Full Shipping Policy →</Link>
        </div>
      ),
    },
    {
      question: 'What is your returns & refunds policy?',
      answer: (
        <div className="space-y-3">
          <p>You have <strong>14 days</strong> to cancel your order (cooling-off period) and an additional <strong>14 days</strong> to return items.</p>
          <p><strong>Conditions:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Items must be unworn</li>
            <li>Returned in original condition</li>
          </ul>
          <p>Refunds are processed within 14 days and include standard delivery. Customer pays return costs unless faulty.</p>
          <p><strong>Faulty items:</strong> 30-day full refund right, repair/replacement after.</p>
          <p><strong>Non-returnable:</strong> Custom items and final sale items.</p>
          <Link to="/returns-policy" className="inline-block text-[#2F5D8C] hover:underline font-semibold mt-2">Read Full Returns Policy →</Link>
        </div>
      ),
    },
    {
      question: 'How do I know what size to order?',
      answer: (
        <div className="space-y-3">
          <p><strong>Vintage sizing differs from modern sizing!</strong></p>
          <p>Each item includes detailed measurements:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Chest/Bust</strong> - measured flat across</li>
            <li><strong>Length</strong> - from shoulder to hem</li>
            <li><strong>Waist</strong> - at narrowest point</li>
          </ul>
          <p>Compare with your own garments for the best fit. If unsure, contact us before purchasing!</p>
          <Link to="/sizing-guide" className="inline-block text-[#2F5D8C] hover:underline font-semibold mt-2">View Full Sizing Guide →</Link>
        </div>
      ),
    },
    {
      question: 'What condition are the items in?',
      answer: (
        <div className="space-y-3">
          <p>All items are carefully inspected and graded before listing:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Excellent</strong> - Minimal to no visible wear</li>
            <li><strong>Very Good</strong> - Light wear consistent with age</li>
            <li><strong>Good</strong> - Visible wear but still wearable</li>
          </ul>
          <p>All flaws are shown in images and described in the product listing.</p>
          <Link to="/product-condition" className="inline-block text-[#2F5D8C] hover:underline font-semibold mt-2">Read Product Condition Policy →</Link>
        </div>
      ),
    },
    {
      question: 'How do you ensure authenticity?',
      answer: (
        <div className="space-y-3">
          <p>All items are:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Hand-selected</strong> by our expert team</li>
            <li><strong>Quality checked</strong> for materials, construction, labels, stitching, and period details</li>
          </ul>
          <p>No counterfeit items knowingly sold. If you have any concerns about authenticity, please contact us directly.</p>
          <Link to="/authenticity" className="inline-block text-[#2F5D8C] hover:underline font-semibold mt-2">Read Authenticity Policy →</Link>
        </div>
      ),
    },
    {
      question: 'Are the items truly one-off pieces?',
      answer: (
        <div className="space-y-3">
          <p><strong>Yes!</strong> Items are unique and not restocked.</p>
          <p>When a product shows <strong>"Only 1 available"</strong> - that's it. Once sold, it's gone forever.</p>
          <p>This is what makes vintage shopping special - you own a piece of fashion history that no one else has.</p>
          <p>We add new items regularly, so check back often or sign up for our newsletter!</p>
          <Link to="/one-of-a-kind" className="inline-block text-[#2F5D8C] hover:underline font-semibold mt-2">Read One-of-a-Kind Policy →</Link>
        </div>
      ),
    },
    {
      question: 'Can I cancel or modify my order?',
      answer: (
        <div className="space-y-3">
          <p>Orders can be cancelled before dispatch.</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Contact us immediately with your order number</li>
            <li>If your order hasn't been dispatched yet, we'll do our best to accommodate your request</li>
            <li>Once dispatched, the order cannot be cancelled (but you can return it upon delivery)</li>
          </ul>
          <Link to="/order-cancellation" className="inline-block text-[#2F5D8C] hover:underline font-semibold mt-2">Read Order Cancellation Policy →</Link>
        </div>
      ),
    },
    {
      question: 'What payment methods do you accept?',
      answer: (
        <div className="space-y-3">
          <p>We accept:</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 my-3">
            <div className="bg-white/80 px-3 py-2 rounded-lg text-center font-semibold text-sm text-[#0A0A0A]">Visa</div>
            <div className="bg-white/80 px-3 py-2 rounded-lg text-center font-semibold text-sm text-[#0A0A0A]">Mastercard</div>
            <div className="bg-white/80 px-3 py-2 rounded-lg text-center font-semibold text-sm text-[#0A0A0A]">Apple Pay</div>
            <div className="bg-white/80 px-3 py-2 rounded-lg text-center font-semibold text-sm text-[#0A0A0A]">Google Pay</div>
          </div>
          <p>Secure payments only. All transactions are processed through encrypted payment gateways. We do not store your payment details.</p>
          <p className="text-sm flex items-center gap-2 mt-2 text-[#5C8A5E]"><Lock className="w-4 h-4" /> SSL Protected & PCI Compliant</p>
          <Link to="/payment-policy" className="inline-block text-[#2F5D8C] hover:underline font-semibold mt-2">Read Payment Policy →</Link>
        </div>
      ),
    },
    {
      question: 'How can I track my order?',
      answer: (
        <div className="space-y-3">
          <p>Once your order is dispatched, you'll receive:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Email confirmation with tracking number</li>
            <li>Direct link to track your parcel</li>
            <li>Courier details and estimated delivery date</li>
          </ul>
          <p>If you haven't received tracking information within 2 working days, please contact us.</p>
          <Link to="/contact" className="inline-block text-[#2F5D8C] hover:underline font-semibold mt-2">Contact Us →</Link>
        </div>
      ),
    },
    {
      question: 'Where can I find your privacy policy?',
      answer: (
        <div className="space-y-3">
          <p>We collect your name, email, and address for order processing. We comply with <strong>UK GDPR</strong>.</p>
          <p>We do not sell or share your personal data with third parties for marketing purposes.</p>
          <Link to="/privacy-policy" className="inline-block text-[#2F5D8C] hover:underline font-semibold mt-2">Read Full Privacy Policy →</Link>
        </div>
      ),
    },
  ];

  return (
    <div className="relative min-h-screen" data-testid="faq-page">
      <SEO title="FAQ" description="Frequently asked questions about Vintage Wardrobe Co — shipping, returns, sizing, authenticity, and more." path="/faq" />
      {/* Fixed Background */}
      <div className="fixed inset-0 z-0">
        <img
          src="https://customer-assets.emergentagent.com/job_6b1653b0-543f-41b7-adb3-d521cbe0d309/artifacts/q4bduyo4_ChatGPT%20Image%20Mar%2022%2C%202026%2C%2011_10_51%20AM.png"
          alt=""
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Content */}
      <div className="relative z-10 py-12 md:py-16">
        <div className="container-custom max-w-3xl">

          {/* Title */}
          <div className="text-center mb-8 bg-[#0A0A0A]/80 backdrop-blur-md py-8 px-6 rounded-2xl border border-white/10">
            <h1 className="text-4xl md:text-6xl font-heading font-bold tracking-tight mb-4 text-white" data-testid="faq-title">
              Frequently Asked Questions
            </h1>
            <p className="text-base text-white/60" data-testid="faq-subtitle">
              Everything you need to know about shopping with us
            </p>
          </div>

          {/* Accordion */}
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-[#F9F8F6]/90 backdrop-blur-md border border-[#E2E2E2] rounded-xl px-6 shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-shadow duration-300"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-bold text-base md:text-lg text-[#0A0A0A] hover:text-[#2F5D8C] transition-colors py-5" data-testid={`faq-question-${index}`}>
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#5A5247] leading-relaxed pb-5" data-testid={`faq-answer-${index}`}>
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* All Policies Grid */}
          <div className="mt-10 bg-[#F9F8F6]/90 backdrop-blur-md p-8 rounded-xl border border-[#E2E2E2] shadow-[0_2px_12px_rgba(0,0,0,0.04)]" data-testid="faq-policies-links">
            <h2 className="text-2xl font-heading font-bold mb-5 text-[#0A0A0A]">All Our Policies</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <Link to="/shipping-policy" className="text-[#2F5D8C] hover:text-[#1e3d5c] hover:underline font-semibold text-sm">Shipping Policy</Link>
              <Link to="/returns-policy" className="text-[#2F5D8C] hover:text-[#1e3d5c] hover:underline font-semibold text-sm">Returns & Refunds</Link>
              <Link to="/sizing-guide" className="text-[#2F5D8C] hover:text-[#1e3d5c] hover:underline font-semibold text-sm">Sizing Guide</Link>
              <Link to="/product-condition" className="text-[#2F5D8C] hover:text-[#1e3d5c] hover:underline font-semibold text-sm">Product Condition</Link>
              <Link to="/authenticity" className="text-[#2F5D8C] hover:text-[#1e3d5c] hover:underline font-semibold text-sm">Authenticity</Link>
              <Link to="/one-of-a-kind" className="text-[#2F5D8C] hover:text-[#1e3d5c] hover:underline font-semibold text-sm">One-of-a-Kind Items</Link>
              <Link to="/order-cancellation" className="text-[#2F5D8C] hover:text-[#1e3d5c] hover:underline font-semibold text-sm">Order Cancellation</Link>
              <Link to="/payment-policy" className="text-[#2F5D8C] hover:text-[#1e3d5c] hover:underline font-semibold text-sm">Payment Policy</Link>
              <Link to="/privacy-policy" className="text-[#2F5D8C] hover:text-[#1e3d5c] hover:underline font-semibold text-sm">Privacy Policy</Link>
              <Link to="/terms-conditions" className="text-[#2F5D8C] hover:text-[#1e3d5c] hover:underline font-semibold text-sm">Terms & Conditions</Link>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-6 text-center bg-[#0A0A0A]/90 backdrop-blur-md p-10 rounded-xl border border-white/10">
            <h2 className="text-3xl font-heading font-bold mb-4 text-white">Still have questions?</h2>
            <p className="text-white/60 mb-6">We're here to help! Get in touch with our team.</p>
            <Link to="/contact">
              <button className="bg-white text-[#0A0A0A] px-8 py-3 text-sm uppercase tracking-widest font-bold hover:bg-[#F9F8F6] transition-colors duration-300">
                Contact Us
              </button>
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
};
